import React from "react";
import { ArrowRight, FileCheck2 } from "lucide-react";
import { inr } from "../lib/format";

export const Hero = ({ onCta, stats }) => {
  return (
    <section id="top" className="relative paper-noise border-b border-border overflow-hidden">
      <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 border border-border bg-white px-3 py-1.5">
            <span className="h-1.5 w-1.5 bg-signal" />
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              For Udyam-registered Micro &amp; Small Enterprises
            </span>
          </div>
          <h1 className="mt-6 font-serif text-5xl sm:text-6xl font-semibold tracking-tight text-ink leading-[1.05]">
            Your buyer owes you.<br />
            The law owes you <span className="text-signal">interest</span>.
          </h1>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground max-w-xl">
            Baaki tracks your overdue B2B invoices, auto-computes statutory interest at
            <span className="font-mono text-ink"> 3× the RBI Bank Rate</span> under Section 16 of the
            MSMED Act, 2006, and prepares a demand notice you can serve — then escalate through
            MSEFC / Samadhaan if the buyer still won't pay.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-btn"
              onClick={onCta}
              className="group inline-flex items-center gap-2 bg-ink text-white text-sm font-medium px-6 py-3.5 hover:bg-black hover:-translate-y-[1px] transition-transform duration-150"
            >
              Join the founding waitlist
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform duration-150" strokeWidth={1.5} />
            </button>
            <a
              href="#demo"
              data-testid="hero-demo-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-ink border-b border-ink/40 hover:border-ink pb-0.5 transition-colors duration-150"
            >
              See the live demo
            </a>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-10 gap-y-4">
            <div>
              <div className="font-mono text-2xl font-semibold text-ink">45 days</div>
              <div className="text-xs text-muted-foreground mt-0.5">Statutory credit-period cap</div>
            </div>
            <div>
              <div className="font-mono text-2xl font-semibold text-ink">3×</div>
              <div className="text-xs text-muted-foreground mt-0.5">RBI Bank Rate, monthly rests</div>
            </div>
            <div>
              <div className="font-mono text-2xl font-semibold text-signal">
                {stats ? inr(stats.total_overdue || 0) : "—"}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">Overdue logged by waitlist</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="bg-white border border-border doc-shadow p-5">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Case Summary · Demo
              </span>
              <FileCheck2 className="h-4 w-4 text-signal" strokeWidth={1.5} />
            </div>
            <div className="pt-4 space-y-3">
              <Row label="Principal outstanding" value={inr(450000)} />
              <Row label="Days overdue" value="182" />
              <Row label="Statutory rate (§16)" value="20.25% p.a." />
              <Row label="Interest accrued" value={inr(46331)} accent />
              <div className="border-t border-border pt-3 flex items-center justify-between">
                <span className="text-sm font-medium text-ink">Recoverable total</span>
                <span className="font-mono text-lg font-semibold text-ink">{inr(496331)}</span>
              </div>
            </div>
            <div className="mt-4 bg-success-bg border border-success/30 px-3 py-2 flex items-center justify-between">
              <span className="text-xs text-success font-medium">Case strength</span>
              <span className="font-mono text-xs text-success">90 / 100 · Strong</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Row = ({ label, value, accent }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`font-mono text-sm ${accent ? "text-signal font-medium" : "text-ink"}`}>{value}</span>
  </div>
);
